import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MoreHorizontal, MoreVertical } from 'lucide-react';

export interface KebabItem {
  label: string;
  onSelect: () => void;
  icon?: ReactNode;
  disabled?: boolean;
}

interface KebabMenuProps {
  items: KebabItem[];
  icon?: 'horizontal' | 'vertical';
  align?: 'start' | 'end';
  size?: 'sm' | 'md';
  ariaLabel?: string;
  className?: string;
}

export function KebabMenu({
  items,
  icon = 'horizontal',
  align = 'end',
  size = 'md',
  ariaLabel = 'Flere handlinger',
  className
}: KebabMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!open) return;
      const target = e.target as Node;
      if (rootRef.current && !rootRef.current.contains(target)) {
        setOpen(false);
      }
    }
    function onKey(e: any) {
      if (!open) return;
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && firstItemRef.current) {
      firstItemRef.current.focus();
    }
  }, [open]);

  const padding = size === 'sm' ? 'p-1.5' : 'p-2';
  const menuPadding = size === 'sm' ? 'py-1' : 'py-2';
  const itemPadding = size === 'sm' ? 'px-2 py-1.5' : 'px-3 py-2';

  return (
    <div ref={rootRef} className={className} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label={ariaLabel}
        className={`rounded hover:bg-gray-100 transition-colors ${padding}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(v => !v);
        }}
     >
        {icon === 'horizontal' ? (
          <MoreHorizontal className="w-5 h-5 text-gray-600" />
        ) : (
          <MoreVertical className="w-5 h-5 text-gray-600" />
        )}
      </button>
      {open && (
        <div className={`relative ${align === 'end' ? 'text-right' : ''}`}>
          <div
            className={`absolute ${align === 'end' ? 'right-0' : 'left-0'} mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-20 ${menuPadding}`}
            role="menu"
            aria-orientation="vertical"
          >
            {items.map((item, idx) => (
              <button
                key={idx}
                role="menuitem"
                ref={idx === 0 ? firstItemRef : undefined}
                disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return;
                  setOpen(false);
                  item.onSelect();
                }}
                className={`w-full text-left ${itemPadding} hover:bg-gray-50 flex items-center gap-2 disabled:text-gray-300 disabled:cursor-not-allowed`}
              >
                {item.icon}
                <span className="text-sm">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default KebabMenu;


